import { Spinner, Tag, Text } from "@ttc3k/trekker";
import {
  EnumTagEntityTypes,
  EnumTagGroupKey,
  EnumTagGroupType,
  TagManyQuery,
  useTagGroupManyQuery,
  useTagManyQuery,
} from "gql/generated";
import _ from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "shared/components";
import { getMultilingualString } from "shared/utils";
import { Flex } from "styled-system/jsx";

type SelectBusinessTypeTagsProps = {
  onSelectTag: (tagId: string) => void;
  selectedTags: string[];
  currentBusinessType: EnumTagGroupKey;
};

export const SelectBusinessTypeTags = ({
  onSelectTag,
  selectedTags,
  currentBusinessType,
}: SelectBusinessTypeTagsProps) => {
  const { t } = useTranslation();

  const {
    data: businessTypeTagGroups,
    loading: businessTypeTagGroupsLoading,
    error: businessTypeTagGroupsError,
  } = useTagGroupManyQuery({
    variables: {
      filter: {
        type: [EnumTagGroupType.BusinessType],
      },
    },
  });

  const [tags, setTags] = useState<TagManyQuery["tagMany"]>([]);
  const { loading: tagsLoading, error: tagsError } = useTagManyQuery({
    variables: {
      filter: {
        tagGroup: businessTypeTagGroups
          ? businessTypeTagGroups.tagGroupMany
              // Filter only the tagGroups where the key matches the currently
              // selected businessType. If that changes so do the tags shown to
              // the user:
              .filter((tagGroup) => tagGroup.key === currentBusinessType)
              .map((tagGroup) => tagGroup._id)
          : [],

        entityTypes: [EnumTagEntityTypes.Operator],
      },
      limit: 10000,
    },
    onCompleted: (data) => {
      if (data.tagMany && data.tagMany.length > 0) {
        setTags(
          _.sortBy(data.tagMany, (tag) => {
            const name = getMultilingualString(tag.name).toLowerCase();
            // When sorting, add prefix of zzz to put names that
            // start with a number at the bottom of the list
            return /^\d/.test(name) ? `zzz${name}` : name;
          }),
        );
      } else {
        setTags([]);
      }
    },
    notifyOnNetworkStatusChange: true,
  });

  if (businessTypeTagGroupsError || tagsError) {
    throw new Error("Error loading tags or tagGroups");
  }
  return (
    <Flex flexDir={"column"} gap={"150"}>
      <Text as={"label"} visual={"smallMedium"} color={"text.light"}>
        {t("directory:OPERATOR.PROFILE.KEY_INFO.BUSINESS_TYPE_TAGS.LABEL")}
      </Text>
      {businessTypeTagGroupsLoading || tagsLoading ? (
        <Spinner width={"20px"} height={"20px"} />
      ) : tags.length > 0 ? (
        <Flex gap={"100"} flexWrap={"wrap"}>
          {tags.map((tag) => {
            const isSelected = selectedTags.includes(tag._id);
            return (
              <Tag
                key={tag._id}
                visual={isSelected ? "accent.purple" : "primary"}
                mode={isSelected ? "dark" : "light"}
                onClick={() => onSelectTag(tag._id)}
              >
                {getMultilingualString(tag.name)}
              </Tag>
            );
          })}
        </Flex>
      ) : (
        <EmptyState
          text={t(
            "directory:OPERATOR.PROFILE.KEY_INFO.BUSINESS_TYPE_TAGS.EMPTY",
          )}
        />
      )}
    </Flex>
  );
};
